import * as React from 'react';

import { toBoolean } from 'pages/utils/sanitize';
import { DiscountMethod } from 'root/apps/PurchaseOrder/LandedCostControl/types';

interface DiscountHandlerProps {
  discountMethod: DiscountMethod;
  discountIsPercent: 0 | 1 | boolean;
  discountMethodSelect: HTMLSelectElement;
  discountIsPercentSelect: HTMLSelectElement;
  discountPercentValueInput: HTMLInputElement;
  discountMoneyValueInput: HTMLInputElement;
  status: string | undefined;
}

export const DiscountHandler: React.FC<DiscountHandlerProps> = ({
  discountMethod: _discountMethod,
  discountIsPercent: _discountIsPercent,
  discountMethodSelect,
  discountIsPercentSelect,
  discountPercentValueInput,
  discountMoneyValueInput,
  status,
}) => {
  const [discountMethod, setDiscountMethod] = React.useState<DiscountMethod>(_discountMethod);
  const [discountIsPercent, setDiscountIsPercent] = React.useState<boolean>(
    toBoolean(_discountIsPercent)
  );

  // Retrieve parent wrapper div or fallback to input element
  const [discountPercentValueInputWrapper] = React.useState<HTMLElement>(
    (discountPercentValueInput.closest('.percent-field-container') as HTMLElement | null) ||
      discountPercentValueInput
  );
  const [discountMoneyValueInputWrapper] = React.useState<HTMLElement>(
    (discountMoneyValueInput.closest('.money-field-container') as HTMLElement | null) ||
      discountMoneyValueInput
  );

  // Bind onchange event to select elements
  React.useEffect(() => {
    discountMethodSelect.addEventListener('change', () => {
      const discountMethod = discountMethodSelect.value as DiscountMethod;
      setDiscountMethod(discountMethod);
    });
    discountIsPercentSelect.addEventListener('change', () => {
      const discountIsPercent = discountIsPercentSelect.value === '1';
      setDiscountIsPercent(discountIsPercent);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Alter UI based on discount method and discount is percent
  React.useEffect(() => {
    const resetUI = () => {
      discountPercentValueInputWrapper.classList.add('hidden');
      discountMoneyValueInputWrapper.classList.remove('hidden');
      discountMoneyValueInputWrapper.classList.remove('invisible');
      discountIsPercentSelect.disabled = status === 'finished';
    };
    const handleDiscountIsPercent = () => {
      if (discountIsPercent) {
        discountMoneyValueInputWrapper.classList.add('hidden');
        discountPercentValueInputWrapper.classList.remove('hidden');
      }
      discountIsPercentSelect.value = discountIsPercent ? '1' : '0';
    };
    const handleDiscountMethod = () => {
      switch (discountMethod) {
        case 'proportionally':
          discountIsPercentSelect.disabled = true;
          setDiscountIsPercent(false);
          break;
        case 'manually':
          discountPercentValueInputWrapper.classList.add('hidden');
          discountMoneyValueInputWrapper.classList.remove('hidden');
          discountMoneyValueInputWrapper.classList.add('invisible');
          break;
        case 'total':
        case 'evenly':
        default:
          break;
      }
      discountMethodSelect.value = discountMethod;
    };
    const updateUI = () => {
      // call order matters
      resetUI();
      handleDiscountIsPercent();
      handleDiscountMethod();
    };

    updateUI();
  }, [
    discountMethod,
    discountIsPercent,
    discountMethodSelect,
    discountIsPercentSelect,
    discountMoneyValueInputWrapper,
    discountPercentValueInputWrapper,
    setDiscountIsPercent,
    status,
  ]);

  return null;
};
